/**
 * Phase B2 — Frontend LocaleContext.
 *
 * Hydrated at app bootstrap from /api/auth/me. The hook `useLocale()` returns
 * a stable object that pages call directly:
 *
 *   const { locale, formatDate, formatDateTime, formatTime, formatCurrency } = useLocale();
 *
 * The locale itself comes from the backend's `locale_resolver.resolve_locale()`
 * (user override → org default → "en-GB"), surfaced as `effective_locale` on
 * the auth/me payload. We keep one source of truth — the server — so the
 * Org Settings dropdown takes effect immediately on every device after a
 * single auth/me refresh.
 *
 * Anonymous flows (public landing, login, signup) get DEFAULT_LOCALE. They
 * NEVER read the context value because the provider's `value` always
 * returns sensible defaults until hydration completes.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  DEFAULT_LOCALE,
  formatDate as _fmtDate,
  formatDateTime as _fmtDateTime,
  formatTime as _fmtTime,
  formatRelative as _fmtRelative,
  formatCurrency as _fmtCurrency,
  getCurrencyForLocale as _curForLocale,
} from "@/lib/locale";

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  // Default to en-GB until hydrated. Updated via setLocale() on /me bootstrap
  // and on user/org locale changes (Org Settings, Account Profile).
  const [locale, setLocale] = useState(DEFAULT_LOCALE);

  // Bind the locale to the formatter signatures so callers don't need to
  // pass it explicitly. Memoised so the value identity is stable across
  // renders that don't change `locale`.
  const value = useMemo(() => ({
    locale,
    setLocale: (l) => setLocale(l || DEFAULT_LOCALE),
    formatDate:     (d) => _fmtDate(d, locale),
    formatDateTime: (d) => _fmtDateTime(d, locale),
    formatTime:     (d) => _fmtTime(d, locale),
    formatRelative: (d) => _fmtRelative(d, locale),
    formatCurrency: (minor, cur) => _fmtCurrency(minor, cur, locale),
    currencyForLocale: () => _curForLocale(locale),
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  // Outside the provider (very rare — anonymous pages) we return a static
  // en-GB binding. Lets shared components render without a provider during
  // public flows without throwing.
  return getStaticDefaultBinding();
}

// Module-level cache so the fallback binding's identity is stable.
// Named without a `use` prefix because it is NOT a React hook — calling it
// conditionally from useLocale() is intentional and safe (it consults a
// module-level cache and returns a plain object).
let _defaultBinding = null;
function getStaticDefaultBinding() {
  if (_defaultBinding) return _defaultBinding;
  _defaultBinding = {
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    formatDate:     (d) => _fmtDate(d, DEFAULT_LOCALE),
    formatDateTime: (d) => _fmtDateTime(d, DEFAULT_LOCALE),
    formatTime:     (d) => _fmtTime(d, DEFAULT_LOCALE),
    formatRelative: (d) => _fmtRelative(d, DEFAULT_LOCALE),
    formatCurrency: (minor, cur) => _fmtCurrency(minor, cur, DEFAULT_LOCALE),
    currencyForLocale: () => _curForLocale(DEFAULT_LOCALE),
  };
  return _defaultBinding;
}

// Convenience hook for cases where we hold a ref to the setter (e.g. the
// AuthProvider needs to re-hydrate the locale when /me returns).
export function useLocaleSetter() {
  const ctx = useContext(LocaleContext);
  // Calling outside the provider is a no-op so AuthProvider mounting order
  // doesn't matter.
  return useCallback((loc) => { if (ctx) ctx.setLocale(loc); }, [ctx]);
}
